import {
  closestCenter,
  DndContext,
  DndContextProps,
  DragOverlay,
  KeyboardSensor,
  MeasuringStrategy,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  SortableContextProps,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Stack, Typography } from "@mui/material";
import {
  EmptyState,
  ListX,
  LoadingIndicator,
  ButtonProps,
  useFoundationColorTokens,
  useSpacing,
  useTypography,
  Snackbar,
} from "@surya-digital/leo-reactjs-material-ui";
import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Plus } from "lucide-react";
import {
  useGetSurveyRulesStore,
  useSaveSurveyRulesStore,
  useSurveyRuleStore,
} from "../../stores/Hooks";
import { SingleSurveyRuleCard } from "../cards/SingleSurveyRuleCard";
import { GroupSurveyRuleCard } from "../cards/GroupSurveyRuleCard";
import { SurveyRuleActionButton } from "../buttons/SurveyRuleActionButtons";
import { SurveyRuleDragOverlayCard } from "../cards/SurveyRuleDragOverlayCard";
import { SaveSurveyRulesRPC } from "@pulse/pulse-rpcs/lib/survey-rules/saveSurveyRulesRPC";
import { useRouteNavigator } from "../../../../routes/RoutesHelper";
import { ErrorDialog } from "../../../../components/dialogs/ErrorDialog";
import { UnexpectedErrorDialog } from "../../../../components/dialogs/UnexpectedErrorDialog";

const DRAG_OVERLAY_Z_INDEX = 1000;

export const SurveyRulesTab = observer((): React.ReactElement => {
  const { t } = useTranslation();
  const tokens = useFoundationColorTokens();
  const typography = useTypography();
  const spacing = useSpacing();
  const navigateTo = useRouteNavigator();

  const [activeRuleId, setActiveRuleId] = useState<string | null>(null);

  const surveyRuleStore = useSurveyRuleStore();
  const saveSurveyRulesStore = useSaveSurveyRulesStore();
  const getSurveyRulesStore = useGetSurveyRulesStore();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  useEffect(() => {
    if (
      saveSurveyRulesStore.saveSurveyRulesRPCError ===
      SaveSurveyRulesRPC.RPCError.InvalidSurveyId
    ) {
      navigateTo.pageNotFound();
    }
  }, [navigateTo, saveSurveyRulesStore.saveSurveyRulesRPCError]);

  const surveyRules: SortableContextProps["items"] =
    surveyRuleStore.rulesOrder.map((ruleId) => ruleId);
  const activeRule = activeRuleId
    ? surveyRuleStore.rules.get(activeRuleId)
    : undefined;
  const isSurveyRuleListEmpty = surveyRuleStore.rulesOrder.length === 0;

  const addRuleButtonProps: ButtonProps = {
    name: "addRule",
    label: t("surveys.rules.addRule"),
    variant: "outlined-color",
    size: "small",
    icon: <Plus />,
    onClick: () => surveyRuleStore.addNewRule("SINGLE"),
  };

  const handleDragStart: DndContextProps["onDragStart"] = ({ active }) => {
    setActiveRuleId(active.id.toString());
  };

  const handleDragEnd: DndContextProps["onDragEnd"] = ({ active, over }) => {
    if (over && active.id !== over.id) {
      const order = surveyRuleStore.rulesOrder.indexOf(over.id.toString());
      surveyRuleStore.reorderRules(active.id.toString(), order);
    }
    setActiveRuleId(null);
  };

  const handleDragCancel: DndContextProps["onDragCancel"] = () => {
    setActiveRuleId(null);
  };

  if (getSurveyRulesStore.loadingGetSurveyRules) {
    return (
      <LoadingIndicator
        variant="container"
        loadingText={t("common.detailsLoadingState")}
        isLoading
      />
    );
  }

  if (isSurveyRuleListEmpty) {
    return (
      <Stack justifyContent="center" height="100%" width="100%">
        <EmptyState
          icon={<ListX />}
          title={t("surveys.rules.noRulesAddedYet")}
          description={t("surveys.rules.noRulesDescription")}
          width="100%"
          buttonProps={addRuleButtonProps}
        />
      </Stack>
    );
  }

  return (
    <>
      <Stack
        height="100%"
        width="100%"
        paddingInline={spacing.space2XL}
        paddingBlock={spacing.spaceXL}
        gap={spacing.spaceMD}
      >
        <Typography color={tokens.labelSubtle} {...typography.b2}>
          {t("surveys.rules.ruleExecutionDescription")}
        </Typography>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          measuring={{
            droppable: {
              strategy: MeasuringStrategy.Always,
            },
          }}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <SortableContext
            items={surveyRules}
            strategy={verticalListSortingStrategy}
          >
            {surveyRuleStore.rulesOrder.map((ruleId) => {
              const rule = surveyRuleStore.getNonNullRule(ruleId);
              return rule.conditions.size === 1 ? (
                <SingleSurveyRuleCard key={ruleId} surveyRule={rule} />
              ) : (
                <GroupSurveyRuleCard key={ruleId} surveyRule={rule} />
              );
            })}
          </SortableContext>
          {createPortal(
            <DragOverlay dropAnimation={null} zIndex={DRAG_OVERLAY_Z_INDEX}>
              {activeRule && (
                <SurveyRuleDragOverlayCard surveyRule={activeRule} />
              )}
            </DragOverlay>,
            document.body,
          )}
        </DndContext>
      </Stack>
      <SurveyRuleActionButton />

      <Snackbar
        isOpen={saveSurveyRulesStore.saveSurveyRulesSnackbarState === "SUCCESS"}
        message={t("surveys.rules.saveSuccess")}
        onClose={saveSurveyRulesStore.hideSaveSurveyRulesSnackbar}
        severity="success"
        isDismissible={true}
      />
      <Snackbar
        isOpen={
          saveSurveyRulesStore.saveSurveyRulesSnackbarState ===
          "VALIDATION_ERROR"
        }
        message={t("surveys.rules.validationErrorSnackbar")}
        onClose={saveSurveyRulesStore.hideSaveSurveyRulesSnackbar}
        severity="error"
        isDismissible={true}
      />
      <Snackbar
        isOpen={saveSurveyRulesStore.maxLimitExceededError !== null}
        message={t("surveys.rules.maxLimitExceededError", {
          maxLimit: saveSurveyRulesStore.maxLimitExceededError ?? 0,
        })}
        onClose={saveSurveyRulesStore.hideMaxLimitExceededSnackbar}
        severity="error"
        isDismissible={true}
      />
      {saveSurveyRulesStore.isProjectAlreadyArchivedErrorDialogVisible && (
        <ErrorDialog
          title={t("common.projectAlreadyArchived")}
          message={t("common.projectAlreadyArchivedErrorText")}
          onClickClose={
            saveSurveyRulesStore.hideProjectAlreadyArchivedErrorDialog
          }
        />
      )}
      {saveSurveyRulesStore.isUnexpectedValidationErrorDialogVisible && (
        <UnexpectedErrorDialog
          message={t("common.uploadDialog.unexpectedErrorDescription")}
          onClickDone={saveSurveyRulesStore.hideUnexpectedValidationErrorDialog}
        />
      )}
    </>
  );
});
